import { IconArrowsVertical } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, {
    useCallback, useEffect, useState, useMemo,
} from 'react';
import RenderIf from 'components/RenderIf/RenderIf';

import NSTable from 'bricks/NSTable/NSTable';
import { sortDocuments, sortItems } from 'views/DocumentLibrary/helpers';
import SortingIcon from '../../../../components/DraggableTable/DraggableHeader/SortingIcon/SortingIcon';
import './DocumentLibraryTable.scss';
import {
    IDealDocument, IDealDirectory, DealDirectoryItem,
} from '../../types';
import { DocumentLibraryColumn } from '../../constants';
import FileTileCard from './FileTileCard';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import FileRow from './FileRow';
import DropzoneCard from '../DropzoneCard/DropzoneCard';

export interface IProps {
    data: IDealDirectory;
    isCardView: boolean;
    visibleColumns: Record<string, boolean>;
    onDocumentSelect: (id: number) => void
    onDocumentEdit: (id: number) => void;
    getFilesFromEvent: (event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>, dropLocation: string) => Promise<File[]>;
    setCurrentDirCardView: (directoryId: number) => void;
    setItemToDelete: (item: DealDirectoryItem | null) => void;
    setItemToUpdate: (item: DealDirectoryItem | null) => void;
}

const DocumentTable = ({
    data,
    isCardView,
    visibleColumns,
    onDocumentSelect,
    onDocumentEdit,
    getFilesFromEvent,
    setCurrentDirCardView,
    setItemToDelete,
    setItemToUpdate,
}: IProps): JSX.Element => {
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const [allExpanded, setAllExpanded] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null);
    const [cardViewData, setCardViewData] = useState<(DealDirectoryItem)[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<IDealDirectory[]>([]);

    const handleToggle = (id: number) => {
        setExpanded(prev => {
            const next = { ...prev, [id]: !prev[id] };
            return next;
        });
    };

    const handleExpandAll = () => {
        const newExpandedState = !allExpanded;
        setAllExpanded(newExpandedState);

        const getAllFolderIds = (items: IDealDirectory[]): number[] => items.reduce<number[]>((acc, item) => {
            if (item.children?.length) {
                if (item.directoryId) acc.push(item.directoryId);

                acc.push(...getAllFolderIds(item.children));
            }
            return acc;
        }, []);

        const folderIds = getAllFolderIds(data.children);
        const newExpanded = folderIds.reduce(
            (acc, id) => {
                acc[id] = newExpandedState;
                return acc;
            },
            {} as { [key: string]: boolean },
        );

        setExpanded(newExpanded);
    };

    const handleSort = (column: string) => {
        setSortConfig(prevConfig => {
            if (!prevConfig || prevConfig.column !== column) {
                return { column, direction: 'asc' };
            }
            if (prevConfig.direction === 'asc') {
                return { column, direction: 'desc' };
            }
            return null; // Reset sorting
        });
    };

    const sortedItems = useMemo(() => [
        ...sortItems(data.children, sortConfig),
        ...sortDocuments(data.documents, sortConfig),
    ], [data.children, data.documents, sortConfig]);

    const showAll = useMemo(() => {
        const values = Object.values(visibleColumns);
        return values.every(value => value === true) || values.every(value => value === false);
    }, [visibleColumns]);

    useEffect(() => {
        if (breadcrumb.length) {
            const selectedDirectory = findDirectoryById(breadcrumb[breadcrumb.length - 1].directoryId, sortedItems);
            if (!selectedDirectory) return;
            setCardViewData([
                ...sortItems(selectedDirectory.children || [], sortConfig),
                ...sortDocuments(selectedDirectory.documents, sortConfig),
            ]);
        } else {
            setCardViewData(sortedItems);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        setCurrentDirCardView(breadcrumb.length ? breadcrumb[breadcrumb.length - 1].directoryId : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breadcrumb]);

    const findDirectoryById = (
        directoryId: number, items: (IDealDirectory | IDealDocument)[],
    ): IDealDirectory | null => items.reduce<IDealDirectory | null>((found, doc) => {
        if (found) return found; // return immediately
        if ('children' in doc) {
            if (doc.directoryId === directoryId) return doc;
            if (doc.children) return findDirectoryById(directoryId, doc.children);
        }
        return null;
    }, null);

    const handleCardViewToggle = useCallback((directoryId: number) => {
        const selectedDirectory = findDirectoryById(directoryId, sortedItems);

        if (!selectedDirectory) return;

        setCardViewData([
            ...sortItems(selectedDirectory.children || [], sortConfig),
            ...sortDocuments(selectedDirectory.documents, sortConfig),
        ]);

        setBreadcrumb(prev => {
            const existingIndex = prev.findIndex(doc => doc.directoryId === directoryId);
            return existingIndex !== -1 ? prev.slice(0, existingIndex + 1) : [...prev, selectedDirectory];
        });

        handleToggle(directoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleToggle, sortedItems]);

    const handleBreadcrumbClick = (index: number) => {
        if (index === 0) {
            setBreadcrumb([]);
            setCardViewData(sortedItems);
        } else {
            const newBreadcrumb = breadcrumb.slice(0, index);
            const selectedFolder = newBreadcrumb[index - 1];

            if (selectedFolder) {
                setBreadcrumb(newBreadcrumb);
                setCardViewData([
                    ...sortItems(selectedFolder.children || [], sortConfig),
                    ...sortDocuments(selectedFolder.documents, sortConfig),
                ]);
            }
        }
    };

    const renderColumnHeader = (label: string) => (
        <div className="d-flex align-items-center justify-content-between cursor--pointer" role="button" tabIndex={0} aria-label={`Sort by ${label}`}>
            <span>{label}</span>
            <SortingIcon
                isSorted={sortConfig?.column === label}
                sortedDirection={sortConfig?.column === label ? sortConfig.direction : null}
                columnId={label.toLowerCase().replace(' ', '-')}
                onAscendingSort={() => handleSort(label)}
                onDescendingSort={() => handleSort(label)}
            />
        </div>
    );

    return (
        <>
            <RenderIf isTrue={!isCardView}>
                <NSTable>
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            {(!showAll && !visibleColumns[DocumentLibraryColumn.NAME]) && (
                                <th className="NSTable__thead__tr__th DocumentLibraryTable__actions-column py-1 text-center">
                                    <NSIcon
                                        icon={IconArrowsVertical}
                                        className="DocumentLibraryTable__expand-btn cursor--pointer"
                                        onClick={handleExpandAll}
                                        title={allExpanded ? 'Collapse all folders' : 'Expand all folders'}
                                    />
                                </th>
                            )}
                            {(showAll || visibleColumns[DocumentLibraryColumn.NAME]) && (
                                <th className="NSTable__thead__tr__th py-1">
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                        <div className="d-flex align-items-center">
                                            <NSIcon
                                                icon={IconArrowsVertical}
                                                className="DocumentLibraryTable__expand-btn cursor--pointer mr-2"
                                                onClick={handleExpandAll}
                                                title={allExpanded ? 'Collapse all folders' : 'Expand all folders'}
                                            />
                                            <span>{DocumentLibraryColumn.NAME}</span>
                                        </div>
                                        <SortingIcon
                                            isSorted={sortConfig?.column === 'name'}
                                            sortedDirection={sortConfig?.column === 'name' ? sortConfig.direction : null}
                                            columnId="name"
                                            onAscendingSort={() => handleSort('name')}
                                            onDescendingSort={() => handleSort('name')}
                                        />
                                    </div>
                                </th>
                            )}
                            {(showAll || visibleColumns[DocumentLibraryColumn.TYPE])
                            && <th className="NSTable__thead__tr__th py-1">{renderColumnHeader(DocumentLibraryColumn.TYPE)}</th>}
                            {(showAll || visibleColumns[DocumentLibraryColumn.FORMAT])
                            && <th className="NSTable__thead__tr__th py-1">{renderColumnHeader(DocumentLibraryColumn.FORMAT)}</th>}
                            {(showAll || visibleColumns[DocumentLibraryColumn.DATE_MODIFIED])
                            && <th className="NSTable__thead__tr__th py-1">{renderColumnHeader(DocumentLibraryColumn.DATE_MODIFIED)}</th>}
                            {(showAll || visibleColumns[DocumentLibraryColumn.SIZE])
                            && <th className="NSTable__thead__tr__th py-1">{renderColumnHeader(DocumentLibraryColumn.SIZE)}</th>}
                            <th className="NSTable__thead__tr__th py-1 DocumentLibraryTable__actions-column" />
                        </tr>
                    </thead>
                    <tbody className="NSTable__tbody">
                        {sortedItems.map(item => (
                            <FileRow
                                key={'id' in item ? `doc-${item.id}` : `dir-${item.directoryId}`}
                                visibleColumns={visibleColumns}
                                showAllColumns={showAll}
                                item={item}
                                level={0}
                                expanded={expanded}
                                onToggle={handleToggle}
                                onEdit={setItemToUpdate}
                                onSelect={onDocumentSelect}
                                onDocumentEdit={onDocumentEdit}
                                onDelete={setItemToDelete}
                            />
                        ))}
                    </tbody>
                </NSTable>
            </RenderIf>

            <RenderIf isTrue={isCardView}>
                <div className="NSCard--level-1 p-2">
                    {breadcrumb.length > 0 && <BreadcrumbNavigation breadcrumb={breadcrumb} handleBreadcrumbClick={handleBreadcrumbClick} />}
                    <div className="DocumentLibraryTable--card-view ">
                        <div className="DocumentLibraryTable__dropzone">
                            <DropzoneCard getFilesFromEvent={getFilesFromEvent} />
                        </div>
                        {cardViewData.map(item => (
                            <FileTileCard
                                key={'id' in item ? `doc-${item.id}` : `dir-${item.directoryId}`}
                                item={item}
                                onToggle={handleCardViewToggle}
                                onSelectDocument={onDocumentSelect}
                                onDocumentEdit={onDocumentEdit}
                                onDelete={() => setItemToDelete(item)}
                                onEdit={setItemToUpdate}
                            />
                        ))}
                    </div>
                </div>
            </RenderIf>
        </>
    );
};

export default DocumentTable;
